/**
 * The closed component set — §5.1, and the hardest thing in Phase 1 to loosen
 * later.
 *
 * Seven components: **callout, embed, table, task list, code, image, and the
 * live-data embed**, plus the standard block and inline nodes markdown already
 * has words for. §5.1 caps the set at six to eight and ADR 0002 explains why
 * the cap is the feature: "an open component system — round-tripping breaks the
 * first time someone adds a component, at which point markdown silently becomes
 * a lossy export and AI legibility degrades without anyone noticing, because
 * nothing fails loudly."
 *
 * This file is the single definition. The editor in `apps/web`, the Hocuspocus
 * instance in `apps/collab`, and the serializer next door all build from these
 * extensions, so a component that exists in one and not the others cannot
 * happen.
 *
 * ## Where this narrows Tiptap's defaults, and why
 *
 * Every attribute the schema keeps must have somewhere to live in markdown, or
 * §5.1's round trip is lossless only for documents nobody wrote. Four of
 * Tiptap's defaults have nowhere to go and are removed rather than silently
 * dropped at serialization time:
 *
 * - `tableCell.colwidth` — column widths are editor layout state, not document
 *   content. Removing the attribute is why **column resizing must stay off**
 *   (`Table.configure({ resizable: false })`, which is already the default).
 * - `tableCell` / `tableHeader` content is `text*`, not `block+`. A GFM table
 *   cell holds inline content and nothing else, so a cell that could contain a
 *   list is a cell whose contents markdown cannot carry. `text*` also makes a
 *   hard break inside a cell unrepresentable, which is correct — GFM has no
 *   spelling for one.
 * - `orderedList.type` (`a`, `i`, …) and `image.width` / `height` — presentation
 *   with no markdown spelling.
 * - `link.target` / `rel` / `class` — markdown carries a href and a title.
 *
 * `colspan` and `rowspan` stay, because `prosemirror-tables` computes a table's
 * geometry from them and a cell without them breaks the editing plugin. GFM
 * cannot express a merged cell, so the serializer refuses one loudly instead.
 */

import { getSchema, mergeAttributes, Node } from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import { BulletList, ListItem, OrderedList, TaskItem, TaskList } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Strike from '@tiptap/extension-strike';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import Text from '@tiptap/extension-text';
import UniqueID from '@tiptap/extension-unique-id';
import { generateBlockId, ID_BEARING_TYPES } from './ids.ts';
import { type DocumentJSON, DocumentSchemaError, type NodeJSON } from './types.ts';

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

/** Closed, and closed on purpose — a fifth variant is a schema change. */
export const CALLOUT_VARIANTS = ['note', 'tip', 'warning', 'danger'] as const;
export type CalloutVariant = (typeof CALLOUT_VARIANTS)[number];

/**
 * An aside: `:::callout{variant=warning}` … `:::`.
 *
 * The content expression lists its children rather than saying `block+` so that
 * a callout cannot contain a callout. Nesting is not a feature anyone asked
 * for, and a nested `:::` fence would need fence-length bookkeeping in the
 * parser for no gain.
 */
const Callout = Node.create({
  name: 'callout',
  group: 'block',
  defining: true,
  content:
    '(paragraph | heading | bulletList | orderedList | taskList | codeBlock | blockquote | image | table | embed | liveData)+',
  addAttributes() {
    return {
      variant: {
        default: 'note' satisfies CalloutVariant,
        parseHTML: (element) => element.getAttribute('data-variant') ?? 'note',
        renderHTML: (attributes) => ({ 'data-variant': attributes.variant }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'aside[data-callout]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['aside', mergeAttributes(HTMLAttributes, { 'data-callout': '' }), 0];
  },
});

/**
 * A third-party thing rendered in place: `::embed{url=… title=…}`.
 *
 * Distinct from the `link` node type in §4.5, which is a node in the tree with
 * its own ACL row. This is content inside a document — a Figma frame, a Loom, a
 * staging URL — and it inherits the document's permissions the same way an
 * embedded asset does (§4.7).
 */
const Embed = Node.create({
  name: 'embed',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      url: { default: null },
      title: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-embed]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-embed': '' })];
  },
});

/** How a live-data result is laid out. Closed, like the callout variants. */
export const LIVE_DATA_FORMATS = ['value', 'table', 'list'] as const;
export type LiveDataFormat = (typeof LIVE_DATA_FORMATS)[number];

/**
 * Reserved attribute names on a live-data embed, **permanently**.
 *
 * §5.1: "design the live-data embed syntax now, while nothing uses it, to avoid
 * migrating later." This list is the part that makes that true. Every attribute
 * outside it is a parameter, so a future capability — a new source, a new
 * argument, a filter — is a new parameter and never a new reserved word. Adding
 * a reserved word later would reinterpret documents already written, which is
 * precisely the migration §5.1 is trying to avoid.
 */
export const LIVE_DATA_RESERVED_ATTRIBUTES: readonly string[] = ['source', 'format'];

/**
 * A placeholder resolved at render time: `::live-data{source=… format=… …params}`.
 *
 * Nothing populates one in Phase 1 — §13 defers the data behind it to Phase 3
 * and later — and that is the reason to fix the syntax now, while there are
 * zero documents to migrate.
 *
 * `source` names a query the way §11.2 names an MCP tool: a business action with
 * constrained inputs, never a generic query interface. `project.time_summary`
 * is a source; a SQL string is not, and the parser has no way to express one.
 * That symmetry is not decoration — the live-data resolver and the MCP tool
 * layer will end up reading from the same registry of named actions.
 */
/**
 * `params` as an HTML attribute, and back.
 *
 * Every other attribute in this schema is a string, so Tiptap's default
 * rendering — write the value, read it back — is correct for all of them.
 * `params` is an object, and the default would write `params="[object Object]"`
 * and parse it back as that literal string. That is not a theoretical loss:
 * ProseMirror's clipboard serialises through the DOM, so copying a live-data
 * block and pasting it would replace the map with a string, and the next write
 * would either throw in the serializer or store the wrong markdown.
 *
 * Exported so the encoding can be tested without a DOM, which this repo has no
 * harness for.
 */
export function serializeLiveDataParams(params: unknown): string {
  return JSON.stringify(params ?? {});
}

/**
 * The inverse, and deliberately total.
 *
 * Anything that is not an object of scalars becomes the empty map rather than
 * an exception. This runs inside ProseMirror's paste path, where a throw is a
 * broken editor rather than a message anyone sees — and a live-data block whose
 * parameters were mangled by an intermediate application is better pasted
 * without them than not pasted at all.
 */
export function parseLiveDataParams(raw: string | null | undefined): Record<string, string> {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string') params[key] = value;
    }
    return params;
  } catch {
    return {};
  }
}

const LiveData = Node.create({
  name: 'liveData',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      source: { default: null },
      format: { default: 'value' satisfies LiveDataFormat },
      /** Free-form scalars. Values are strings; the resolver coerces. */
      params: {
        default: {} as Record<string, string>,
        renderHTML: (attributes) => ({
          'data-params': serializeLiveDataParams(attributes.params),
        }),
        parseHTML: (element) => parseLiveDataParams(element.getAttribute('data-params')),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-live-data]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-live-data': '' })];
  },
});

// ---------------------------------------------------------------------------
// The extension list
// ---------------------------------------------------------------------------

/**
 * Every extension in the closed set, in one array.
 *
 * `apps/web` adds the editor-only extensions on top of this — placeholder,
 * collaboration cursor, keyboard shortcuts — and must not add anything that
 * defines a node or a mark. A node type that exists in the editor and not here
 * serializes to nothing.
 */
export const documentExtensions = [
  Document,
  Paragraph,
  Text,
  Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
  Blockquote,
  CodeBlock,
  HardBreak,
  HorizontalRule,
  BulletList,
  OrderedList.extend({
    addAttributes() {
      return { start: { default: 1 } };
    },
  }),
  ListItem,
  TaskList,
  // `nested` widens the item from `paragraph+` to `paragraph block*`, matching
  // `listItem`. A checklist with sub-items is the ordinary shape of a delivery
  // checklist, markdown writes it with two spaces, and the alternative is the
  // author discovering the restriction mid-sentence.
  TaskItem.configure({ nested: true }),
  Table,
  TableRow,
  TableHeader.extend({
    content: 'text*',
    addAttributes() {
      return { colspan: { default: 1 }, rowspan: { default: 1 } };
    },
  }),
  TableCell.extend({
    content: 'text*',
    addAttributes() {
      return { colspan: { default: 1 }, rowspan: { default: 1 } };
    },
  }),
  Image.extend({
    addAttributes() {
      return { src: { default: null }, alt: { default: null }, title: { default: null } };
    },
  }),
  Callout,
  Embed,
  LiveData,
  Bold,
  Italic,
  Strike,
  Code,
  Link.extend({
    addAttributes() {
      return { href: { default: null }, title: { default: null } };
    },
  }),
  UniqueID.configure({
    types: [...ID_BEARING_TYPES],
    generateID: generateBlockId,
  }),
];

/**
 * The ProseMirror schema, built once.
 *
 * Built from the extension list rather than written out by hand so there is one
 * definition of the component set, not a Tiptap one and a ProseMirror one that
 * drift. `getSchema` touches no DOM, which is what lets the API and the
 * markdown parser use it on Bun.
 */
export const documentSchema = getSchema(documentExtensions);

// ---------------------------------------------------------------------------
// Validation and normalization
// ---------------------------------------------------------------------------

const LIST_TYPES: ReadonlySet<string> = new Set(['bulletList', 'orderedList', 'taskList']);

/**
 * Merges two lists of the same type that sit next to each other.
 *
 * Markdown cannot tell them apart — two bulleted lists separated by a blank
 * line are one list, in every reader there is — so leaving them separate would
 * make the round trip lossy for a document ProseMirror itself joins the moment
 * anyone puts a cursor between them. Normalising here means both directions
 * agree, and the merge is idempotent.
 */
function mergeAdjacentLists(node: NodeJSON): NodeJSON {
  if (!node.content) return node;
  const merged: NodeJSON[] = [];
  for (const child of node.content.map(mergeAdjacentLists)) {
    const previous = merged[merged.length - 1];
    if (previous && previous.type === child.type && LIST_TYPES.has(child.type)) {
      merged[merged.length - 1] = {
        ...previous,
        content: [...(previous.content ?? []), ...(child.content ?? [])],
      };
      continue;
    }
    merged.push(child);
  }
  return { ...node, content: merged };
}

/**
 * Round-trips JSON through the schema: rejects anything outside the closed set,
 * merges adjacent lists, and fills in default attributes so two documents that
 * mean the same thing compare equal.
 *
 * Both directions of the round trip pass through here. Without it, "lossless"
 * would be a claim about a shape nothing checks — a node type invented by a
 * future editor extension would serialize to nothing and the loss would show up
 * as a missing paragraph, months later, in a document nobody was watching.
 */
export function normalizeDocument(json: unknown): DocumentJSON {
  const candidate = json as NodeJSON;
  if (!candidate || typeof candidate !== 'object' || candidate.type !== 'doc') {
    throw new DocumentSchemaError('a document must be a `doc` node');
  }
  let node: ReturnType<typeof documentSchema.nodeFromJSON>;
  try {
    node = documentSchema.nodeFromJSON(mergeAdjacentLists(candidate));
    node.check();
  } catch (cause) {
    throw new DocumentSchemaError(
      `document does not fit the closed component set: ${(cause as Error).message}`,
      { cause },
    );
  }
  return node.toJSON() as DocumentJSON;
}

/** The document a new node starts with: one empty paragraph, already anchored. */
export function emptyDocument(): DocumentJSON {
  return {
    type: 'doc',
    content: [{ type: 'paragraph', attrs: { id: generateBlockId() } }],
  };
}
